import { getEmployeesOfDepartment } from '../../../helpers/api-ts/users'
import { EditShiftPlanningFormData, ShiftPlanningViewModel } from '../../../models'
import { BaseScheduleShift } from '../../entities/BaseScheduleShift/BaseScheduleShift'
import { mergePlanningIntoShift } from '../../entities/BaseScheduleShiftPlanning/BaseScheduleShiftPlanningHelpers'
import {
    deleteBaseScheduleShiftPlanningApiCall,
    patchBaseScheduleShiftPlanningApiCall,
    postBaseScheduleShiftPlanningApiCall,
} from '../../entities/BaseScheduleShiftPlanning/BaseScheduleShiftPlanningService'
import {
    transformAddBaseScheduleShiftPlanningToV3Request,
    transformBaseScheduleShiftPlanningToV3CopyRequest,
    transformEditBaseScheduleShiftPlanningColorToV3PatchRequest,
    transformEditBaseScheduleShiftPlanningFormDataToV3PatchRequest,
} from '../../entities/BaseScheduleShiftPlanning/BaseScheduleShiftPlanningTransformers'
import { isFetchResultSuccessful } from '../../entities/FetchResult'
import { setBaseScheduleShift } from '../baseScheduleShift/baseScheduleShiftReducer'
import { TypedDispatch } from '../store'
import {
    setEmployeeOptions,
    setError,
    setIsLoading,
    setIsSuccessful,
} from './baseScheduleShiftPlanningReducer'

export const planUserToBaseScheduleShift = (
    baseScheduleShift: BaseScheduleShift,
    userId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformAddBaseScheduleShiftPlanningToV3Request(baseScheduleShift.id, userId)
        const response = await postBaseScheduleShiftPlanningApiCall(request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseScheduleShift(mergePlanningIntoShift(baseScheduleShift, response.data)))
    } catch (error) {
        console.error('[planUserToBaseScheduleShift]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const setBaseScheduleShiftPlanningColor = (
    baseScheduleShift: BaseScheduleShift,
    shiftPlanning: ShiftPlanningViewModel,
    color?: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformEditBaseScheduleShiftPlanningColorToV3PatchRequest(color)
        const response = await patchBaseScheduleShiftPlanningApiCall(shiftPlanning.id, request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseScheduleShift(mergePlanningIntoShift(baseScheduleShift, response.data, shiftPlanning.id)))
    } catch (error) {
        console.error('[setBaseScheduleShiftPlanningColor]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const copyBaseScheduleShiftPlanning = (
    baseScheduleShiftPlanning: ShiftPlanningViewModel,
    baseScheduleShiftId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformBaseScheduleShiftPlanningToV3CopyRequest(baseScheduleShiftPlanning, baseScheduleShiftId)
        const response = await postBaseScheduleShiftPlanningApiCall(request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[copyBaseScheduleShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editBaseScheduleShiftPlanning = (
    baseScheduleShift: BaseScheduleShift,
    shiftPlanningId: string,
    formData: EditShiftPlanningFormData,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const request = transformEditBaseScheduleShiftPlanningFormDataToV3PatchRequest(formData)
        const response = await patchBaseScheduleShiftPlanningApiCall(shiftPlanningId, request)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseScheduleShift(mergePlanningIntoShift(baseScheduleShift, response.data, shiftPlanningId)))
    } catch (error) {
        console.error('[editBaseScheduleShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const deleteBaseScheduleShiftPlanning = (
    baseScheduleShift: BaseScheduleShift,
    shiftPlanningId: string,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await deleteBaseScheduleShiftPlanningApiCall(shiftPlanningId)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseScheduleShift({
            ...baseScheduleShift,
            shiftPlannings: baseScheduleShift.shiftPlannings.filter(planning => planning.id !== shiftPlanningId),
        }))
    } catch (error) {
        console.error('[deleteBaseScheduleShiftPlanning]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const fetchEmployeeOptions = (department: string) => async (dispatch: TypedDispatch): Promise<void> => {
    try {
        const response = await getEmployeesOfDepartment(
            department,
            [
                'person',
                'roles',
            ],
        )
        dispatch(setEmployeeOptions(response))
    } catch (error) {
        console.error('[fetchEmployeeOptions]', error)
    }
}
